import React, { useCallback, useContext, useRef, useState } from "react";
import Typography from "components/elements/Typography";
import { ValidationError } from "common/resources/Resource";
import { FormContext, FormProvider } from "./FormContext";
import { isNonEmpty } from "common/utils/array";

export type IProps = {
	children: React.ReactNode;
	name?: string;
	onSubmit?: (formData: { [key: string]: unknown }, e?: React.FormEvent<HTMLFormElement>) => void;
	errors?: ValidationError[];
	className?: string;
	onChange?: (e: React.ChangeEvent<HTMLFormElement>) => void;
	formRef?: React.RefObject<HTMLFormElement>;
};

export default function Form(props: IProps) {
	return (
		<FormProvider errors={props.errors ?? []}>
			<SubForm onSubmit={props.onSubmit} className={props.className} formRef={props.formRef} onChange={props.onChange}>
				{props.children}
			</SubForm>
		</FormProvider>
	);
}

function SubForm(props: IProps) {
	const context = useContext(FormContext);
	const formError = context.getMessagesErrors("form");
	const onSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (props.onSubmit) props.onSubmit(getFormData(e), e);
		},
		[props],
	);

	return (
		<form onSubmit={onSubmit} name={props.name} className={props.className} onChange={props.onChange} ref={props.formRef}>
			{props.children}
			{formError.length > 0 && (
				<Typography type="p" size="small" weight="medium" color="error">
					{formError}
				</Typography>
			)}
		</form>
	);
}

/**
 * Extracts form data from an HTMLFormElement during a form event.
 * @param {React.FormEvent<HTMLFormElement>} e - The form event.
 * @returns {Record<string, unknown>} The formData object containing form inputs.
 */
function getFormData(e: React.FormEvent<HTMLFormElement>): Record<string, unknown> {
	const elements = e.currentTarget.elements;
	const formData: Record<string, unknown> = {};

	Array.from(elements).forEach((element) => {
		if (element instanceof HTMLInputElement) {
			const { name, type, checked, value, id } = element;

			if (!name) return; // Skip elements without a name attribute to ensure consistent keying.

			switch (type) {
				case "checkbox":
					formData[id] = checked;
					break;
				case "file":
					formData[name] = element.files;
					break;
				case "radio":
					if (checked) formData[name] = value;
					break;
				default:
					formData[name] = value;
			}
		}

		if (element instanceof HTMLTextAreaElement) {
			const { name, value } = element;

			if (!name) return; // Skip elements without a name attribute to ensure consistent keying.

			formData[name] = value;
		}

		if (element instanceof HTMLSelectElement) {
			const { name, value } = element;

			if (!name) return; // Skip elements without a name attribute to ensure consistent keying.

			formData[name] = value;
		}
	});

	return formData;
}

export const useForm = () => {
	const formRef = useRef<HTMLFormElement | null>(null);
	const [errors, setErrors] = useState<ValidationError[]>([]);

	const clearErrors = useCallback(() => {
		setErrors([]);
	}, []);

	const handleError = useCallback((error: unknown) => {
		const flattenErrors = (error: ValidationError): ValidationError[] => {
			if (!isNonEmpty(error.children)) return [error];

			return error.children.flatMap((child) =>
				flattenErrors(child).map((e) => ({
					...e,
					property: error.property,
				})),
			);
		};

		console.error({ error });
		if (error instanceof Array) {
			setErrors(error.flatMap(flattenErrors));
		}
	}, []);

	return { formRef, errors, handleError, clearErrors };
};
